import { useEffect } from 'react';
// @mui
import { styled } from '@mui/material/styles';
import { Box, AppBar, Toolbar, IconButton } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

// utils
import { bgBlur } from '../../../utils/cssStyles';
// components
import Iconify from '../../../components/iconify';
import Toast from '../../../components/toast';
import Banner from '../../../components/banner';
//
import { clearError } from '../../../slices/error';
import type { RootState } from '../../../store';

// ----------------------------------------------------------------------

const NAV_WIDTH = 260;
const NAV_COLLAPSED_WIDTH = 80;

const HEADER_MOBILE = 36;

const HEADER_DESKTOP = 48;

const StyledRoot = styled(AppBar)(({ theme }) => ({
	...bgBlur({ color: theme.palette.background.default }),
	boxShadow: 'none',
	[theme.breakpoints.up('sm')]: {
		width: `calc(100% - ${NAV_COLLAPSED_WIDTH}px)`,
	},
	[theme.breakpoints.up('lg')]: {
		width: `calc(100% - ${NAV_WIDTH}px)`,
	},
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
	minHeight: HEADER_MOBILE,
	height: HEADER_MOBILE,
	backgroundColor: '#EFF6FF',
	[theme.breakpoints.up('sm')]: {
		minHeight: HEADER_DESKTOP,
		height: HEADER_DESKTOP,
	},
	[theme.breakpoints.up('lg')]: {
		minHeight: HEADER_DESKTOP,
		height: HEADER_DESKTOP,
	},
}));

// ----------------------------------------------------------------------

interface HeaderProps {
	onOpenNav: () => void;
	showBanner: boolean;
}

export default function Header({ onOpenNav, showBanner }: HeaderProps) {
	const globalError = useSelector((state: RootState) => state.error?.error?.message);
	const dispatch = useDispatch();
	const { pathname } = useLocation();

	const handleSnackbarClose = () => {
		if (globalError) {
			dispatch(clearError());
		}
	};

	// clear error state when user navigates away from current route
	useEffect(() => {
		if (pathname) {
			// work around the warning that pathname is not used in the effect
		}
		dispatch(clearError());
	}, [pathname, dispatch]);

	return (
		<StyledRoot>
			{showBanner && (
				<Banner>
					2 New Locations Now Accepting Bookings: <b>Eastwood</b> & <b>Campbelltown</b>
				</Banner>
			)}

			<StyledToolbar>
				<IconButton
					onClick={onOpenNav}
					sx={{
						mr: 1,
						color: 'text.primary',
						display: { xs: 'inline-flex', sm: 'none' },
					}}
				>
					<Iconify icon="eva:menu-2-fill" />
				</IconButton>

				{globalError && <Toast onClose={handleSnackbarClose} title="Error" body={globalError} severity="error" />}

				<Box sx={{ flexGrow: 1 }} />
			</StyledToolbar>
		</StyledRoot>
	);
}
